import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { CircleCheck } from 'lucide-react';
import { AuthCard } from '@/components/marketing/auth-card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { auth } from '@/lib/auth';

export const Route = createFileRoute('/signup')({ component: SignupPage });

const PERKS = [
  'Invoicing, expenses, banking and GST in one place',
  'Share the same books with your accountant',
  'Reports by class, location and project',
];

function SignupPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!auth.enabled) {
      void navigate({ to: '/dashboard' });
      return;
    }
    setBusy(true);
    void auth
      .signIn('/dashboard', { email: email.trim(), signUp: true })
      .catch(() => setBusy(false));
  };

  return (
    <AuthCard
      title="Sign up to continue"
      subtitle="Create your Yedo Books account with your work email."
      footer={
        <span className="text-brand-slate">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-primary hover:underline">
            Log in
          </Link>
        </span>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <label className="block">
          <span className="text-xs font-semibold text-brand-slate">Work email</span>
          <Input
            type="email"
            autoComplete="email"
            autoFocus
            required={auth.enabled}
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 h-10 bg-white"
            data-testid="signup-email"
          />
        </label>
        <ul className="space-y-2 rounded-md bg-[#f7f8f9] p-4">
          {PERKS.map((p) => (
            <li key={p} className="flex items-start gap-2 text-xs text-brand-navy">
              <CircleCheck className="mt-px size-3.5 shrink-0 text-success" />
              {p}
            </li>
          ))}
        </ul>
        <p className="text-xs leading-relaxed text-brand-slate">
          By signing up, you agree to the terms of use and privacy policy of Gear Up Studios.
        </p>
        <Button type="submit" className="h-10 w-full font-semibold" disabled={busy}>
          {busy ? 'Opening sign-up…' : 'Sign up'}
        </Button>
      </form>
    </AuthCard>
  );
}
